import { Injectable } from '@angular/core';
import { format } from 'date-fns';

import { ReportGenerator } from './report-generator';
import { ReportRow } from './report-generator.model';

import {
  AutoTestVoltageResult as BaseVoltageResult,
  AutoTestFrequencyResult as BaseFrequencyResult,
} from '../../communication/auto-test.service';

export interface AutoTestVoltageResultWithRated extends BaseVoltageResult {
  RatedVoltage: number;
}

export interface AutoTestFrequencyResultWithRated extends BaseFrequencyResult {
  RatedFrequency: number;
}

/** All labels must be pre-translated by the host — no i18n keys in the library */
export interface AutoTestPdfLabels {
  title: string;
  descriptionA: string;
  descriptionB: string;
  cei021Standard: string;
  cei021: string;
  thresholdValueSet: string;
  interventionTimeSet: string;
  detectedThresholdValue: string;
  tripTimeDetected: string;
  result: string;
}

@Injectable({ providedIn: 'root' })
export class AutoTestPdfService {

  async generateReportPdf(
    voltageResults: AutoTestVoltageResultWithRated[],
    frequencyResults: AutoTestFrequencyResultWithRated[],
    labels: AutoTestPdfLabels,
    serial: string,
    logoPath = 'assets/logo.png',
  ) {
    const report = new ReportGenerator();
    await report.addLogo(logoPath);
    report.addTitle(
      `${labels.title} - ${serial}`.toUpperCase(),
      format(new Date(), 'dd/MM/yyyy HH:mm:ss'),
    );
    report.addTextLine(labels.descriptionA, 10);
    report.addTextLine(labels.descriptionB, 10);

    const options = {
      columns: [
        { name: labels.cei021Standard.toUpperCase(), percentageWidth: 25 },
        { name: '', percentageWidth: 25 },
        { name: '', percentageWidth: 25 },
        { name: '', percentageWidth: 25 },
      ],
    };

    const rows: ReportRow[] = [];
    rows.push({
      value: [
        labels.thresholdValueSet,
        labels.interventionTimeSet,
        labels.detectedThresholdValue,
        labels.tripTimeDetected,
      ],
      isSectionRow: true,
    });

    for (const result of voltageResults) {
      rows.push({
        value: [`${labels.cei021.toUpperCase()} - ${result.name}`],
        isSectionRow: true,
      });
      rows.push({
        value: [
          `${result.voltageDefault.toFixed(2)} V (${(result.voltageDefault / result.RatedVoltage).toFixed(2)} Vn)`,
          `${(result.timeDefault / 1000).toFixed(3)} s`,
          `${result.voltageValue.toFixed(2)} V`,
          `${(result.timeValue / 1000).toFixed(3)} s`,
        ],
      });
    }

    for (const result of frequencyResults) {
      rows.push({
        value: [`${labels.cei021.toUpperCase()} - ${result.name}`],
        isSectionRow: true,
      });
      rows.push({
        value: [
          `${result.frequencyDefault.toFixed(2)} Hz`,
          `${(result.timeDefault / 1000).toFixed(3)} s`,
          `${result.frequencyValue.toFixed(2)} Hz`,
          `${(result.timeValue / 1000).toFixed(3)} s`,
        ],
      });
    }

    report.addAttributes(rows, options);
    report.addGap(10);
    report.addTextLine(labels.result, 10);

    await report.save(`${serial}-${format(new Date(), 'dd-MM-yyyy-HH-mm-ss')}.pdf`);
  }
}
